'use client';

import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import { useState } from 'react';
import { Link } from '@/i18n/navigation';
import { useAuth } from '@/shared/providers/AuthProvider';
import { PaddlePlan } from '@/entities/billing/lib/paddlePlans';
import { PADDLE_REFUND_POLICY_VERSION } from '@/entities/billing/lib/paddleRefundPolicy';
import { SubscriptionTier } from '@/entities/billing/lib/billing.constants';
import { usePaddleCheckout } from '@/features/billing/model/PaddleCheckoutProvider';

const PADDLE_IMMEDIATE_SERVICE_NOTICE =
  'The service starts immediately after payment. Consumed AI, OCR, speech, and other metered usage may be considered when determining refund eligibility, subject to Paddle’s Refund Policy and mandatory law. Continue?';
const PADDLE_REFUND_CONSENT = {
  immediateServiceConsent: true as const,
  refundPolicyVersion: PADDLE_REFUND_POLICY_VERSION,
};

const CHECKOUT_ERROR_MESSAGES: Record<string, string> = {
  '국내 구독을 먼저 해지한 뒤 Paddle 결제를 시작해 주세요.':
    'Cancel your domestic subscription before starting a Paddle subscription.',
  '이미 활성화된 Paddle 구독이 있습니다.':
    'Your company already has an active Paddle subscription.',
  '다른 Paddle 요금제 결제가 진행 중입니다.':
    'Checkout for another Paddle plan is already in progress.',
  '진행 중인 Paddle 결제가 있습니다.':
    'A Paddle checkout is already in progress.',
  '회사의 외부 구독 변경을 처리 중입니다.':
    'Your subscription is being updated. Please try again shortly.',
  'Paddle 결제 검토 기간에는 구독을 변경할 수 없습니다. 다음 결제일 이후 다시 시도해 주세요.':
    'Paddle is reviewing your upcoming renewal, so this subscription cannot be changed right now. Please try again after your next billing date.',
};

const DEFAULT_CHECKOUT_ERROR_MESSAGE =
  'We could not start checkout. Please try again.';

const getCheckoutErrorMessage = (error: unknown): string => {
  if (
    !axios.isAxiosError<{ message?: unknown }>(error) ||
    typeof error.response?.data?.message !== 'string'
  ) {
    return DEFAULT_CHECKOUT_ERROR_MESSAGE;
  }
  return (
    CHECKOUT_ERROR_MESSAGES[error.response.data.message] ??
    DEFAULT_CHECKOUT_ERROR_MESSAGE
  );
};

export const PaddleCheckoutButton = ({ plan }: { plan: PaddlePlan }) => {
  const { user } = useAuth();
  const {
    isReady,
    openCheckout,
    previewUpgrade,
    upgradePlan,
    scheduleDowngrade,
    revokeDowngrade,
    subscription,
    subscriptionError,
  } = usePaddleCheckout();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasExistingSubscription = [
    'active',
    'trialing',
    'past_due',
    'paused',
  ].includes(subscription?.status ?? '');
  const isCurrentPlan =
    hasExistingSubscription && subscription?.tier === plan.tier;
  const hasPaymentIssue =
    subscription?.status === 'past_due' || subscription?.status === 'paused';
  const canUpgrade =
    !hasPaymentIssue &&
    subscription?.tier === SubscriptionTier.Starter &&
    plan.tier === SubscriptionTier.Pro;
  const hasPendingDowngrade =
    subscription?.pendingTier === SubscriptionTier.Starter;
  const isStarterCardForProSubscription =
    !hasPaymentIssue &&
    subscription?.tier === SubscriptionTier.Pro &&
    plan.tier === SubscriptionTier.Starter;

  const formatPrice = (amount: string, currencyCode: string): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(Number(amount) / 100);

  const handleCheckout = async () => {
    setErrorMessage(null);
    if (!window.confirm(PADDLE_IMMEDIATE_SERVICE_NOTICE)) return;
    setIsLoading(true);
    try {
      await openCheckout(plan.key, PADDLE_REFUND_CONSENT);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(getCheckoutErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      const preview = await previewUpgrade(plan.key);
      const confirmed = window.confirm(
        `Upgrade to Pro now? Paddle will charge ${formatPrice(preview.immediateCharge, preview.currencyCode)} now. Your next monthly payment will be ${formatPrice(preview.nextRecurringCharge, preview.currencyCode)}.\n\n${PADDLE_IMMEDIATE_SERVICE_NOTICE}`
      );
      if (!confirmed) return;
      await upgradePlan(plan.key, PADDLE_REFUND_CONSENT);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(getCheckoutErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDowngrade = async () => {
    setErrorMessage(null);
    const effectiveDate = subscription?.currentPeriodEnd
      ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
          new Date(subscription.currentPeriodEnd)
        )
      : 'the end of your current billing period';
    const confirmed = window.confirm(
      `Downgrade to Starter on ${effectiveDate}? You will keep Pro access until then. Your current payment will not be refunded.`
    );
    if (!confirmed) return;
    setIsLoading(true);
    try {
      await scheduleDowngrade(plan.key);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(getCheckoutErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeDowngrade = async () => {
    setErrorMessage(null);
    if (!window.confirm('Keep Pro and cancel the scheduled downgrade?')) return;
    setIsLoading(true);
    try {
      await revokeDowngrade();
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(getCheckoutErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <Link
        href="/auth/sign-in"
        className="text-body1 flex h-[48px] w-full items-center justify-center rounded-[12px] bg-blue-600 font-[600] text-white hover:bg-blue-700"
      >
        Sign in to subscribe
      </Link>
    );
  }

  if (subscriptionError) {
    return (
      <button
        type="button"
        disabled
        className="text-body1 flex h-[48px] w-full cursor-not-allowed items-center justify-center rounded-[12px] bg-gray-200 px-[12px] font-[600] text-gray-600"
      >
        Unable to verify subscription. Refresh to try again.
      </button>
    );
  }

  if (subscription === undefined) {
    return (
      <button
        type="button"
        disabled
        className="text-body1 flex h-[48px] w-full cursor-not-allowed items-center justify-center rounded-[12px] bg-gray-200 font-[600] text-gray-600"
      >
        Checking subscription...
      </button>
    );
  }

  if (hasExistingSubscription) {
    if (isStarterCardForProSubscription) {
      return (
        <div>
          <button
            type="button"
            onClick={
              hasPendingDowngrade ? handleRevokeDowngrade : handleDowngrade
            }
            disabled={isLoading}
            className={`text-body1 flex h-[48px] w-full items-center justify-center rounded-[12px] font-[600] ${isLoading ? 'cursor-not-allowed bg-gray-300 text-white' : hasPendingDowngrade ? 'border border-blue-600 bg-white text-blue-600 hover:bg-blue-50' : 'bg-gray-700 text-white hover:bg-gray-800'}`}
          >
            {isLoading
              ? 'Updating plan...'
              : hasPendingDowngrade
                ? 'Cancel downgrade'
                : 'Downgrade next billing period'}
          </button>
          {errorMessage && (
            <p
              role="alert"
              className="text-caption1 mt-[10px] text-center text-red-500"
            >
              {errorMessage}
            </p>
          )}
        </div>
      );
    }
    if (canUpgrade) {
      return (
        <div>
          <button
            type="button"
            onClick={handleUpgrade}
            disabled={isLoading}
            className={`text-body1 flex h-[48px] w-full items-center justify-center rounded-[12px] font-[600] text-white ${isLoading ? 'cursor-not-allowed bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isLoading ? 'Preparing upgrade...' : 'Upgrade to Pro'}
          </button>
          {errorMessage && (
            <p
              role="alert"
              className="text-caption1 mt-[10px] text-center text-red-500"
            >
              {errorMessage}
            </p>
          )}
        </div>
      );
    }
    return (
      <button
        type="button"
        disabled
        className="text-body1 flex h-[48px] w-full cursor-not-allowed items-center justify-center rounded-[12px] bg-gray-200 font-[600] text-gray-600"
      >
        {isCurrentPlan
          ? hasPaymentIssue
            ? 'Payment required'
            : 'Current plan'
          : 'Active subscription'}
      </button>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={handleCheckout}
        disabled={!isReady || isLoading}
        className={`text-body1 flex h-[48px] w-full items-center justify-center rounded-[12px] font-[600] text-white ${!isReady || isLoading ? 'cursor-not-allowed bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'}`}
      >
        {isLoading ? 'Preparing checkout...' : 'Subscribe'}
      </button>
      {errorMessage && (
        <p
          role="alert"
          className="text-caption1 mt-[10px] text-center text-red-500"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
